import { Helmet } from 'react-helmet-async';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useWhitepapers, type WhitepaperCardItem } from '@/hooks/useWhitepapers';
import { usePublications, type PublicationCardItem } from '@/hooks/usePublications';
import Loader from '@/components/Common/Loader';
import { sendGAPageview } from '../utils/gaConsent';
import useText from '@/hooks/useText';
import InsightsHero from './insights/InsightsHero';
import InsightsCard from './insights/InsightsCard';
import './insights/insights.css';

type ResourcesTab = 'whitepapers' | 'publications' | 'all';
type PublicationsSort = 'newest' | 'oldest';

const RESOURCES_TABS: ResourcesTab[] = ['publications', 'whitepapers', 'all'];
const DEFAULT_RESOURCES_TAB: ResourcesTab = 'all';

const parseResourcesTab = (value: string | null): ResourcesTab => {
  if (value && RESOURCES_TABS.includes(value as ResourcesTab)) {
    return value as ResourcesTab;
  }
  return DEFAULT_RESOURCES_TAB;
};

const parseWhitepaperId = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const buildPublicationsPath = (tab: ResourcesTab, whitepaperId?: number | null) => {
  if (whitepaperId) {
    return `/publications/whitepapers/${whitepaperId}`;
  }
  if (tab === DEFAULT_RESOURCES_TAB) {
    return '/publications';
  }
  return `/publications/${tab}`;
};

const readLegacyUrlParams = (search: string, hash: string) => {
  const fromSearch = new URLSearchParams(search);
  const fromHash = new URLSearchParams(hash.replace(/^#/, ''));
  return {
    tab: fromSearch.get('tab') || fromHash.get('tab'),
    whitepaper: fromSearch.get('whitepaper') || fromHash.get('whitepaper'),
  };
};

type CombinedResource =
  | { kind: 'publication'; date: string; year: string; publication: PublicationCardItem }
  | { kind: 'whitepaper'; date: string; year: string; whitepaper: WhitepaperCardItem };

const getResourceYear = (date: string) => {
  const trimmed = date.trim();
  if (/^\d{4}$/.test(trimmed)) return trimmed;

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) return String(parsed.getFullYear());

  const match = trimmed.match(/\b(19|20)\d{2}\b/);
  return match?.[0] ?? '';
};

const parseResourceYear = (year: string) => {
  const value = Number(year);
  return Number.isFinite(value) ? value : 0;
};

const sortResourcesByDate = (items: CombinedResource[], sort: PublicationsSort) =>
  [...items].sort((a, b) =>
    sort === 'newest'
      ? parseResourceYear(b.year) - parseResourceYear(a.year)
      : parseResourceYear(a.year) - parseResourceYear(b.year),
  );

const Whitepapers = () => {
  const publicationsTitle = useText('publications.title');
  const publicationsSubtitle = useText('publications.subtitle');
  const location = useLocation();
  const navigate = useNavigate();
  const { tabId } = useParams<{
    tabId?: string;
  }>();

  const resourcesTab = parseResourcesTab(tabId ?? null);

  const isAllTab = resourcesTab === 'all';
  const isPublicationsTab = resourcesTab === 'publications';
  const isWhitepapersTab = resourcesTab === 'whitepapers';
  const { whitepapers, loading: whitepapersLoading, error: whitepapersError } = useWhitepapers();
  const {
    publications,
    loading: publicationsLoading,
    error: publicationsError,
  } = usePublications(isPublicationsTab || isAllTab);
  const [selectedPublicationYear, setSelectedPublicationYear] = useState<string>('all');
  const [publicationsSort, setPublicationsSort] = useState<PublicationsSort>('newest');
  const publicationYears = useMemo(
    () =>
      Array.from(new Set(publications.map(publication => publication.date)))
        .filter(Boolean)
        .sort((a, b) => Number(b) - Number(a)),
    [publications],
  );

  const allResources = useMemo<CombinedResource[]>(
    () => [
      ...publications.map(publication => ({
        kind: 'publication' as const,
        date: publication.date,
        year: getResourceYear(publication.date),
        publication,
      })),
      ...whitepapers.map(whitepaper => ({
        kind: 'whitepaper' as const,
        date: whitepaper.date,
        year: getResourceYear(whitepaper.date),
        whitepaper,
      })),
    ],
    [publications, whitepapers],
  );

  const allResourceYears = useMemo(
    () =>
      Array.from(new Set(allResources.map(resource => resource.year)))
        .filter(Boolean)
        .sort((a, b) => Number(b) - Number(a)),
    [allResources],
  );

  const filteredPublications = useMemo(() => {
    const byYear =
      selectedPublicationYear === 'all'
        ? publications
        : publications.filter(publication => publication.date === selectedPublicationYear);

    return [...byYear].sort((a, b) =>
      publicationsSort === 'newest' ? Number(b.date) - Number(a.date) : Number(a.date) - Number(b.date),
    );
  }, [publications, selectedPublicationYear, publicationsSort]);

  const filteredAllResources = useMemo(() => {
    const byYear =
      selectedPublicationYear === 'all'
        ? allResources
        : allResources.filter(resource => resource.year === selectedPublicationYear);

    return sortResourcesByDate(byYear, publicationsSort);
  }, [allResources, selectedPublicationYear, publicationsSort]);

  const allResourcesLoading = publicationsLoading || whitepapersLoading;
  const filterYears = isAllTab ? allResourceYears : publicationYears;

  const navigateToPublications = useCallback(
    (tab: ResourcesTab, whitepaperId?: number | null) => {
      navigate(buildPublicationsPath(tab, whitepaperId), { replace: true });
    },
    [navigate],
  );

  const setResourcesTab = useCallback(
    (tab: ResourcesTab) => {
      navigateToPublications(tab);
    },
    [navigateToPublications],
  );

  useEffect(() => {
    const legacy = readLegacyUrlParams(location.search, location.hash);
    if (!legacy.tab && !legacy.whitepaper) return;

    const target = buildPublicationsPath(parseResourcesTab(legacy.tab), parseWhitepaperId(legacy.whitepaper));
    navigate(target, { replace: true });
  }, [location.search, location.hash, navigate]);

  useEffect(() => {
    if (tabId && !RESOURCES_TABS.includes(tabId as ResourcesTab)) {
      navigate('/publications', { replace: true });
    }
  }, [tabId, navigate]);

  useEffect(() => {
    sendGAPageview({ hitType: 'pageview', page: location.pathname, title: 'Publications' });
  }, [location.pathname]);

  const handleOpenForm = (id: number) => {
    navigateToPublications('whitepapers', id);
  };

  const tabClass = (isActive: boolean) =>
    `insights-pill insights-pill--button${isActive ? ' insights-pill--active' : ''}`;

  const renderPublicationCard = (pub: PublicationCardItem) => {
    const href = pub.doiUrl || pub.externalLink;
    return (
      <InsightsCard
        key={`publication-${pub.id}`}
        variant="publication"
        href={href}
        external={Boolean(href)}
        badge={pub.publicationType || 'Publication'}
        title={pub.title}
        description={pub.journal || undefined}
        detail={pub.doi ? `DOI: ${pub.doi}` : undefined}
        metaLeft={pub.date}
        metaRight={href ? (pub.doiUrl ? 'View DOI' : 'Open') : undefined}
      />
    );
  };

  const renderWhitepaperCard = (whitepaper: WhitepaperCardItem) => (
    <InsightsCard
      key={`whitepaper-${whitepaper.id}`}
      variant="whitepaper"
      onClick={() => handleOpenForm(whitepaper.id)}
      badge="Whitepaper"
      title={whitepaper.title}
      description={whitepaper.description || 'Read this whitepaper to see all details.'}
      metaLeft={whitepaper.date}
      metaRight="Read"
    />
  );

  const renderCombinedResourceCard = (resource: CombinedResource) =>
    resource.kind === 'publication'
      ? renderPublicationCard(resource.publication)
      : renderWhitepaperCard(resource.whitepaper);

  return (
    <>
      <Helmet>
        <title>DISGENET: Publications</title>
        <meta
          name="description"
          content="Explore DISGENET whitepapers and scientific publications from MedBioinformatics."
        />
        <meta property="og:title" content="DISGENET: Publications" />
        <meta
          property="og:description"
          content="Explore DISGENET whitepapers and scientific publications from MedBioinformatics."
        />
        <meta property="og:url" content="https://www.disgenet.com/publications" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/publications" />
      </Helmet>

      <div className="insights-page">
        <InsightsHero
          variant="publications"
          eyebrow="Insights"
          title={publicationsTitle}
          subtitle={publicationsSubtitle}
        >
          <div className="insights-hero__tabs" role="tablist" aria-label="Resource library sections">
            <button
              type="button"
              role="tab"
              aria-selected={isAllTab}
              className={tabClass(isAllTab)}
              onClick={() => setResourcesTab('all')}
            >
              All
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={isPublicationsTab}
              className={tabClass(isPublicationsTab)}
              onClick={() => setResourcesTab('publications')}
            >
              Publications
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={isWhitepapersTab}
              className={tabClass(isWhitepapersTab)}
              onClick={() => setResourcesTab('whitepapers')}
            >
              Whitepapers
            </button>
          </div>
          {!isWhitepapersTab ? (
            <>
              <label>
                <span className="sr-only">Filter by year</span>
                <select
                  className="insights-pill"
                  value={selectedPublicationYear}
                  onChange={event => setSelectedPublicationYear(event.target.value)}
                >
                  <option value="all">All years</option>
                  {filterYears.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="sr-only">Order</span>
                <select
                  className="insights-pill"
                  value={publicationsSort}
                  onChange={event => setPublicationsSort(event.target.value as PublicationsSort)}
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                </select>
              </label>
            </>
          ) : null}
        </InsightsHero>

        <div className="insights-content">
          {isAllTab ? (
            <>
              {allResourcesLoading ? (
                <div className="py-12">
                  <Loader text="Loading resources..." isInnerSpinner />
                </div>
              ) : null}

              {!allResourcesLoading && publicationsError ? (
                <p className="insights-error">Publications: {publicationsError}</p>
              ) : null}

              {!allResourcesLoading && whitepapersError ? (
                <p className="insights-error">Whitepapers: {whitepapersError}</p>
              ) : null}

              {!allResourcesLoading && !publicationsError && !whitepapersError ? (
                allResources.length === 0 ? (
                  <p className="insights-empty">
                    No publications or whitepapers are available at the moment.
                  </p>
                ) : filteredAllResources.length === 0 ? (
                  <p className="insights-empty">No resources found for the selected year.</p>
                ) : (
                  <div className="insights-grid">
                    {filteredAllResources.map(resource => renderCombinedResourceCard(resource))}
                  </div>
                )
              ) : null}
            </>
          ) : null}

          {isPublicationsTab ? (
            <>
              {publicationsLoading ? (
                <div className="py-12">
                  <Loader text="Loading publications..." isInnerSpinner />
                </div>
              ) : null}

              {!publicationsLoading && publicationsError ? (
                <p className="insights-error">{publicationsError}</p>
              ) : null}

              {!publicationsLoading && !publicationsError ? (
                publications.length === 0 ? (
                  <p className="insights-empty">
                    No publications with the DISGENET tag are available at the moment.
                  </p>
                ) : filteredPublications.length === 0 ? (
                  <p className="insights-empty">No publications found for the selected year.</p>
                ) : (
                  <div className="insights-grid">
                    {filteredPublications.map(pub => renderPublicationCard(pub))}
                  </div>
                )
              ) : null}
            </>
          ) : null}

          {isWhitepapersTab ? (
            <>
              {whitepapersLoading ? (
                <div className="py-12">
                  <Loader text="Loading whitepapers..." isInnerSpinner />
                </div>
              ) : null}

              {!whitepapersLoading && whitepapersError ? (
                <p className="insights-error">{whitepapersError}</p>
              ) : null}

              {!whitepapersLoading && !whitepapersError ? (
                whitepapers.length === 0 ? (
                  <p className="insights-empty">No whitepapers are available at the moment.</p>
                ) : (
                  <div className="insights-grid">
                    {whitepapers.map(whitepaper => renderWhitepaperCard(whitepaper))}
                  </div>
                )
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default Whitepapers;
